import { useAuth0 } from '@auth0/auth0-react';
import { useMemo } from 'react';
import { NavLink } from 'react-router';
import appRoutes from '~/appRoutes';
import type { NavigationList } from '~/components/granular';
import type { IconType } from '~/components/icons';
import { useDemandTabs } from '~/components/pages/demand-dashboard/hooks/useDemandTabs';
import { useSupplyTabs } from '~/components/pages/supply-dashboard/hooks/useSupplyTabs';
import { useFormatMessage } from '~/intl';
import { useAllocatedVolumesTab } from '~/pages/supplier/allocations/allocated-volumes/useAllocatedVolumesTab';
import { useDeliveryMonitoringTabs } from '~/pages/supplier/delivery-monitoring/useDeliveryMonitoringTabs';
import { useOutboundDeliveryMonitoringTabs } from '~/pages/supplier/outbound-delivery-monitoring/useOutboundDeliveryMonitoringTabs';
import { usePortfolioTabs } from '~/pages/supplier/portfolio-view/hooks/usePortfolioTabs';
import { useDealsEnabled } from './useDealsEnabled';
import { useEsnaEnabled } from './useEsnaEnabled';
import { FEATURE_FLAGS, useFeatureFlag, useFeatureFlagEnabledByDefault } from './useFeatureFlag';
import { useHelpscoutBeacon } from './useHelpscoutBeacon';

export function useNavigation(isEndConsumer: boolean) {
  const dealsEnabled = useDealsEnabled();
  const pnlAndValuationEnabled = useFeatureFlag(FEATURE_FLAGS.pnl_and_valuation_enabled);
  const allocationsPivotTableEnabled = useFeatureFlagEnabledByDefault(FEATURE_FLAGS.allocations_pivot_table_enabled);

  const esnaEnabled = useEsnaEnabled();

  const { logout } = useAuth0();
  const formatMessage = useFormatMessage();
  const { toggleChat } = useHelpscoutBeacon(isEndConsumer);

  const { lastSelectedTab: lastPortfolioTab } = usePortfolioTabs();
  const { lastSelectedTab: lastDemandTab } = useDemandTabs();
  const { lastSelectedTab: lastSupplyTab } = useSupplyTabs();
  const { lastSelectedTab: lastDeliveryMonitoringTab } = useDeliveryMonitoringTabs();
  const { lastSelectedTab: lastOutboundDeliveryMonitoringTab } = useOutboundDeliveryMonitoringTabs();
  const { lastSelectedTab: lastAllocatedVolumesTab } = useAllocatedVolumesTab();

  return useMemo(() => {
    // Step pages sit under the routine editor they belong to, the routine saved or being created alike.
    // Breadcrumbs call `to` with the route's `:id`, or '' for the routine being created — hence turning
    // '' back into no routine id. Matching is by prefix, so the blank step id leaves the `…/steps/` every
    // step page starts with, whichever step it edits. The add link has to stay first: its `…/steps/new`
    // also starts with that, and the first match wins.
    const routineStepLinks = [
      {
        id: 'add-routine-step',
        isHidden: true,
        label: formatMessage({ id: 'navigation.addAllocationRoutineStep' }),
        link: NavLink,
        to: (routineId: string) => appRoutes.ALLOCATION_ROUTINE_NEW_STEP(routineId || undefined),
      },
      {
        id: 'edit-routine-step',
        isHidden: true,
        label: formatMessage({ id: 'navigation.editAllocationRoutineStep' }),
        link: NavLink,
        to: (routineId: string) => appRoutes.ALLOCATION_ROUTINE_EDIT_STEP('', routineId || undefined),
      },
    ];

    const supplierNavigation: Array<NavigationList> = [
      {
        id: 'dashboard',
        label: formatMessage({ id: 'navigation.dashboard' }, { count: 2 }),
        links: [
          {
            breadcrumbRoutes: [
              appRoutes.POSITION_MONITORING,
              appRoutes.POSITION_MONITORING_CUSTOM_VIEWS(),
              appRoutes.MATCHING_ANALYSIS,
            ],
            icon: 'DashboardIcon',
            id: 'portfolio-view',
            isHidden: esnaEnabled,
            label: formatMessage({ id: 'navigation.portfolioView' }),
            link: NavLink,
            to: lastPortfolioTab?.route,
          } as const,
          {
            breadcrumbRoutes: [appRoutes.DEMAND_VIEW, appRoutes.DEMAND_VIEW_CUSTOM_VIEWS()],
            icon: 'FocusIcon',
            id: 'demand-view',
            label: formatMessage({ id: 'navigation.consumerView' }),
            link: NavLink,
            to: lastDemandTab?.route,
          } as const,
          {
            breadcrumbRoutes: [appRoutes.SUPPLY_VIEW, appRoutes.SUPPLY_VIEW_CUSTOM_VIEWS()],
            icon: 'SupplyIcon',
            id: 'supply-view',
            label: formatMessage({ id: 'navigation.supplyView' }),
            link: NavLink,
            to: lastSupplyTab?.route,
          } as const,
        ],
      },
      {
        id: 'portfolio-contracts',
        label: formatMessage({ id: 'navigation.portfolio' }),
        links: [
          {
            icon: 'HotelIcon',
            id: 'customers-and-counterparties',
            label: formatMessage({ id: 'navigation.customersAndCounterparties' }),
            link: NavLink,
            links: [
              {
                id: 'edit-counterparty',
                isHidden: true,
                label: formatMessage({ id: 'counterparties.shared.customers.editCustomer' }),
                link: NavLink,
                to: appRoutes.EDIT_COUNTERPARTY,
              },
              {
                id: 'create-counterparty',
                isHidden: true,
                label: formatMessage({ id: 'counterparties.shared.customers.addNewCustomer' }),
                link: NavLink,
                to: appRoutes.CREATE_COUNTERPARTY,
              },
            ],
            to: appRoutes.CUSTOMERS_AND_COUNTERPARTIES,
          },
          {
            icon: 'RemoteControlIcon',
            id: 'consumption-meter',
            label: formatMessage({ id: 'navigation.consumptionMeters' }),
            link: NavLink,
            to: appRoutes.CONSUMPTION_METERS,
          },
          {
            icon: 'FlashlightIcon',
            id: 'production-devices',
            label: formatMessage({ id: 'common.productionDevice' }, { count: 2 }),
            link: NavLink,
            links: [
              {
                id: 'edit-production-device',
                isHidden: true,
                label: formatMessage({ id: 'productionDevices.list.editDevice' }),
                link: NavLink,
                to: appRoutes.EDIT_PRODUCTION_DEVICE,
              },
              {
                id: 'create-production-device',
                isHidden: true,
                label: formatMessage({ id: 'productionDevices.list.addNewDevice' }),
                link: NavLink,
                to: appRoutes.CREATE_PRODUCTION_DEVICE,
              },
            ],
            to: appRoutes.PRODUCTION_DEVICES,
          },
          ...(dealsEnabled
            ? [
                {
                  icon: 'ExchangeFundsIcon' as IconType,
                  id: 'deals',
                  label: formatMessage({ id: 'deals.shared.deal' }, { count: 2 }),
                  link: NavLink,
                  links: [
                    {
                      id: 'create-deal',
                      isHidden: true,
                      label: formatMessage({ id: 'deals.shared.newDeal' }),
                      link: NavLink,
                      to: appRoutes.CREATE_DEAL,
                    },
                  ],
                  to: appRoutes.DEALS,
                },
              ]
            : []),
        ],
      },
      {
        id: 'certificates',
        isHidden: esnaEnabled,
        label: formatMessage({ id: 'certificates.shared.label' }, { count: 2 }),
        links: [
          {
            icon: 'FileListIcon',
            id: 'inventory',
            label: formatMessage({ id: 'navigation.inventory' }),
            link: NavLink,
            to: appRoutes.INVENTORY(),
          },
          {
            icon: 'FilePaperIcon',
            id: 'transactions',
            label: formatMessage({ id: 'navigation.transactions' }),
            link: NavLink,
            links: [
              {
                id: 'transactions-details',
                isHidden: true,
                label: formatMessage({ id: 'certificates.transactionDetails.title' }),
                link: NavLink,
                to: appRoutes.TRANSACTION_DETAILS,
              },
              {
                id: 'recognise-transaction',
                isHidden: true,
                label: formatMessage({ id: 'certificates.recognise.title' }),
                link: NavLink,
                to: appRoutes.RECOGNISE_TRANSACTION,
              },
              {
                id: 'bulk-recognise-transaction',
                isHidden: true,
                label: formatMessage({ id: 'certificates.recognise.title' }),
                link: NavLink,
                to: appRoutes.BULK_RECOGNISE_TRANSACTION,
              },
              {
                id: 'deal-reconciliation',
                isHidden: true,
                label: formatMessage({ id: 'certificates.dealReconciliation.title' }),
                link: NavLink,
                to: appRoutes.DEAL_RECONCILIATION,
              },
            ],
            to: appRoutes.TRANSACTIONS(),
          },
          {
            icon: 'FolderUserIcon',
            id: 'registry-accounts',
            label: formatMessage({ id: 'registryAccounts.list.registryAccount' }, { count: 2 }),
            link: NavLink,
            links: [
              {
                id: 'create-registry-account',
                isHidden: true,
                label: formatMessage({ id: 'registryAccounts.list.addAnAccountHolder' }),
                link: NavLink,
                to: appRoutes.CREATE_REGISTRY_ACCOUNT,
              },
            ],
            to: appRoutes.REGISTRY_ACCOUNTS,
          },
          {
            breadcrumbRoutes: [
              appRoutes.DELIVERY_MONITORING_DELIVERED_VOLUMES(),
              appRoutes.DELIVERY_MONITORING_CONTRACTED_VOLUMES(),
              appRoutes.DELIVERY_MONITORING_RECONCILED_VOLUMES(),
            ],
            icon: 'InboxArchiveIcon',
            id: 'delivery-monitoring',
            label: formatMessage({ id: 'navigation.deliveryMonitoring' }),
            link: NavLink,
            to: lastDeliveryMonitoringTab?.route,
          },
          {
            breadcrumbRoutes: [
              appRoutes.OUTBOUND_DELIVERY_MONITORING_DELIVERED_VOLUMES(),
              appRoutes.OUTBOUND_DELIVERY_MONITORING_CONTRACTED_VOLUMES(),
              appRoutes.OUTBOUND_DELIVERY_MONITORING_RECONCILED_VOLUMES(),
            ],
            icon: 'InboxUnarchiveIcon',
            id: 'outbound-delivery-monitoring',
            label: formatMessage({ id: 'navigation.outboundDeliveryMonitoring' }),
            link: NavLink,
            to: lastOutboundDeliveryMonitoringTab?.route,
          },
          {
            icon: 'CertificateIcon',
            id: 'dispatched-certificate-ids-attribution',
            label: formatMessage({ id: 'navigation.dispatchedCertificateIdsAttribution' }),
            link: NavLink,
            to: appRoutes.DISPATCHED_CERTIFICATE_IDS_ATTRIBUTION(),
          },
        ],
      },
      {
        id: 'allocations',
        label: formatMessage({ id: 'allocations.shared.label' }, { count: 2 }),
        links: [
          {
            breadcrumbRoutes: [
              // Only show the Allocations Custom Views link if the feature flag is enabled. Otherwise, it will be hidden.
              ...(allocationsPivotTableEnabled ? [appRoutes.ALLOCATIONS_CUSTOM_VIEWS()] : []),
              appRoutes.ALLOCATIONS_BATCHES,
            ].filter(Boolean) as string[],
            icon: 'ExchangeLineIcon' as IconType,
            id: 'allocated-volumes',
            label: formatMessage({ id: 'navigation.allocatedVolumes' }),
            link: NavLink,
            to: allocationsPivotTableEnabled ? lastAllocatedVolumesTab?.route : appRoutes.ALLOCATIONS_BATCHES,
          },
          {
            icon: 'StackIcon' as IconType,
            id: 'routines',
            isHidden: esnaEnabled,
            label: formatMessage({ id: 'navigation.allocationRoutines' }),
            link: NavLink,
            links: [
              {
                id: 'new-routine',
                isHidden: true,
                label: formatMessage({ id: 'navigation.newAllocationRoutine' }),
                link: NavLink,
                to: appRoutes.ALLOCATION_ROUTINE_NEW,
                links: routineStepLinks,
              },
              {
                id: 'edit-routine',
                isHidden: true,
                label: formatMessage({ id: 'navigation.editAllocationRoutine' }),
                link: NavLink,
                to: appRoutes.ALLOCATION_ROUTINE_EDIT,
                links: routineStepLinks,
              },
            ],
            to: appRoutes.ALLOCATION_ROUTINES,
          },
          {
            icon: 'HistoryIcon' as IconType,
            id: 'allocations-history',
            label: allocationsPivotTableEnabled
              ? formatMessage({ id: 'navigation.allocationsHistory' })
              : formatMessage({ id: 'allocations.shared.label' }, { count: 2 }),
            link: NavLink,
            links: [
              {
                id: 'get-run-details',
                isHidden: true,
                label: formatMessage({ id: 'allocations.shared.runDetails' }),
                link: NavLink,
                to: appRoutes.ALLOCATION_RUN_DETAILS,
              },
            ],
            to: appRoutes.ALLOCATIONS_HISTORY,
          },
          {
            icon: 'SoundModuleIcon',
            id: 'create-allocation',
            isHidden: esnaEnabled,
            label: formatMessage({ id: 'navigation.createAllocation' }),
            link: NavLink,
            links: [
              {
                id: 'create-interactive-allocation',
                isHidden: true,
                label: formatMessage({ id: 'navigation.interactiveAllocation' }),
                link: NavLink,
                to: appRoutes.CREATE_ALLOCATION_INTERACTIVE,
              },
              {
                id: 'create-one-off-allocation',
                isHidden: true,
                label: formatMessage({ id: 'navigation.oneOffAllocation' }),
                link: NavLink,
                to: appRoutes.CREATE_ALLOCATION_ONE_OFF,
              },
            ],
            to: appRoutes.CREATE_ALLOCATION,
          },
        ],
      },
      ...(pnlAndValuationEnabled
        ? [
            {
              id: 'pnl-valuation',
              label: 'PnL and valuation',
              links: [
                {
                  icon: 'DashboardIcon' as IconType,
                  id: 'market-data',
                  label: 'Market data',
                  link: NavLink,
                  to: appRoutes.PNL_MARKET_DATA,
                },
                {
                  icon: 'FileChartIcon' as IconType,
                  id: 'mark-to-market',
                  label: 'Mark-to-market',
                  link: NavLink,
                  to: appRoutes.PNL_MARK_TO_MARKET,
                },
                {
                  icon: 'FilePaperIcon' as IconType,
                  id: 'pnl-monitoring',
                  label: 'PnL monitoring',
                  link: NavLink,
                  to: appRoutes.PNL_MONITORING,
                },
              ],
            },
          ]
        : []),
      {
        id: 'reporting',
        isHidden: esnaEnabled,
        label: formatMessage({ id: 'navigation.consumerReporting' }),
        links: [
          {
            icon: 'FileChartIcon',
            id: 'reports',
            label: formatMessage({ id: 'navigation.reports' }),
            link: NavLink,
            links: [
              {
                id: 'generate-report',
                isHidden: true,
                label: formatMessage({ id: 'reports.list.generateReport' }),
                link: NavLink,
                to: appRoutes.GENERATE_BATCH_REPORTS,
              },
            ],
            to: appRoutes.REPORTS,
          },
        ],
      },
      {
        id: 'import-data',
        label: formatMessage({ id: 'navigation.importData' }),
        links: [
          {
            icon: 'UploadIcon',
            id: 'import-metering-data',
            label: formatMessage({ id: 'common.portfolioData' }),
            link: NavLink,
            links: [
              {
                id: 'import-consumption-meters',
                isHidden: true,
                label: formatMessage({ id: 'upload.portfolioData.consumptionMeters.title' }),
                link: NavLink,
                to: appRoutes.IMPORT_CONSUMPTION_METERS,
              },
              {
                id: 'import-production-devices',
                isHidden: true,
                label: formatMessage({ id: 'upload.portfolioData.productionDevices.title' }),
                link: NavLink,
                to: appRoutes.IMPORT_PRODUCTION_DEVICES,
              },
              {
                id: 'import-deals',
                isHidden: true,
                label: formatMessage({ id: 'deals.shared.importDeals' }),
                link: NavLink,
                to: appRoutes.IMPORT_DEALS,
              },
              {
                id: 'import-consumption',
                isHidden: true,
                label: formatMessage({ id: 'upload.portfolioData.consumption.title' }),
                link: NavLink,
                to: appRoutes.IMPORT_CONSUMPTION,
              },
              {
                id: 'import-production',
                isHidden: true,
                label: formatMessage({ id: 'upload.portfolioData.production.title' }),
                link: NavLink,
                to: appRoutes.IMPORT_PRODUCTION,
              },
            ],
            to: appRoutes.IMPORT_PORTFOLIO_DATA,
          },
          {
            icon: 'UploadIcon',
            id: 'import-certificates-data',
            isHidden: esnaEnabled,
            label: formatMessage({ id: 'common.certificatesData' }),
            link: NavLink,
            to: appRoutes.IMPORT_CERTIFICATES_DATA,
          },
        ],
      },
      ...(esnaEnabled
        ? [
            {
              id: 'esna',
              label: formatMessage({ id: 'navigation.esna' }),
              links: [
                {
                  icon: 'CalendarIcon' as IconType,
                  id: 'settlement-calendar',
                  label: formatMessage({ id: 'navigation.settlementCalendar' }),
                  link: NavLink,
                  to: appRoutes.ESNA_EXPECTED_UPLOAD_CALENDAR,
                },
                {
                  icon: 'FileTextIcon' as IconType,
                  id: 'les-contracts',
                  label: formatMessage({ id: 'navigation.lesContracts' }),
                  link: NavLink,
                  links: [
                    {
                      id: 'view-les-contract',
                      isHidden: true,
                      label: formatMessage({ id: 'esna.lesContracts.shared.viewContract' }),
                      link: NavLink,
                      to: appRoutes.ESNA_LES_CONTRACT_DETAILS,
                    },
                    {
                      id: 'edit-les-contract',
                      isHidden: true,
                      label: formatMessage({ id: 'esna.lesContracts.shared.editContract' }),
                      link: NavLink,
                      to: appRoutes.ESNA_LES_CONTRACT_EDIT,
                    },
                    {
                      id: 'create-les-contract',
                      isHidden: true,
                      label: formatMessage({ id: 'esna.lesContracts.shared.createContract' }),
                      link: NavLink,
                      to: appRoutes.ESNA_LES_CONTRACT_NEW,
                    },
                  ],
                  to: appRoutes.ESNA_LES_CONTRACTS,
                },
                {
                  icon: 'PercentIcon' as IconType,
                  id: 'loss-factor-codes',
                  label: formatMessage({ id: 'navigation.lossFactorCodes' }),
                  link: NavLink,
                  links: [
                    {
                      id: 'import-loss-factor-codes',
                      isHidden: true,
                      label: formatMessage({ id: 'esna.lossFactorCodes.import' }),
                      link: NavLink,
                      to: appRoutes.ESNA_LOSS_FACTOR_CODES_IMPORT,
                    },
                  ],
                  to: appRoutes.ESNA_LOSS_FACTOR_CODES,
                },
              ],
            },
          ]
        : []),
      {
        id: 'data-management',
        label: formatMessage({ id: 'navigation.dataManagement' }),
        links: [
          {
            icon: 'TableLineIcon' as IconType,
            id: 'metering-data-coverage',
            label: formatMessage({ id: 'navigation.meteringDataCoverage' }),
            link: NavLink,
            to: appRoutes.METERING_DATA_COVERAGE,
          },
        ],
      },
      {
        id: 'async-operations',
        label: formatMessage({ id: 'navigation.operationsSystem' }),
        links: [
          {
            icon: 'ListSettingsIcon' as IconType,
            id: 'async-operations',
            label: formatMessage({ id: 'asyncOperations.operationsLog' }),
            link: NavLink,
            to: appRoutes.OPERATIONS_LOG,
          },
        ],
      },
    ];

    const consumerNavigation: Array<NavigationList> = [
      {
        id: 'dashboard',
        label: formatMessage({ id: 'navigation.dashboard' }, { count: 1 }),
        links: [
          {
            icon: 'FocusIcon',
            id: 'dashboard',
            label: formatMessage({ id: 'navigation.dashboard' }, { count: 1 }),
            link: NavLink,
            to: appRoutes.DASHBOARD,
          } as const,
        ],
      },
      {
        id: 'reporting',
        label: formatMessage({ id: 'navigation.consumerReporting' }),
        links: [
          {
            icon: 'FileChartIcon',
            id: 'report',
            label: formatMessage({ id: 'navigation.reports' }),
            link: NavLink,
            to: appRoutes.REPORTS,
          },
        ],
      },
    ];

    const footerNavigation: Array<NavigationList> = [
      {
        icon: 'QuestionIcon',
        id: 'support',
        isHidden: isEndConsumer,
        label: formatMessage({ id: 'common.support' }),
        links: [
          {
            href: 'https://support.granular-energy.com/',
            icon: 'SettingsIcon',
            id: 'help',
            label: formatMessage({ id: 'common.helpCenter' }),
            rightIcon: 'ExternalLinkLineIcon',
            target: '_blank',
          },
          {
            icon: 'SettingsIcon',
            id: 'chat',
            label: formatMessage({ id: 'navigation.liveChat' }),
            onClick: toggleChat,
          },
          {
            href: 'https://docs.granular-energy.com/',
            icon: 'SettingsIcon',
            id: 'docs',
            isHidden: isEndConsumer,
            label: formatMessage({ id: 'navigation.documentation' }),
            rightIcon: 'ExternalLinkLineIcon',
            target: '_blank',
          },
        ],
      },
      {
        icon: 'SettingsIcon',
        id: 'settings',
        label: formatMessage({ id: 'common.settings' }),
        links: [
          {
            id: 'settings',
            label: formatMessage({ id: 'common.settings' }),
            link: NavLink,
            to: appRoutes.SETTINGS,
          },
          {
            id: 'team',
            isHidden: isEndConsumer,
            label: formatMessage({ id: 'navigation.team' }),
            link: NavLink,
            to: appRoutes.TEAM,
          },
          {
            id: 'log-out',
            label: formatMessage({ id: 'navigation.logout' }),
            onClick: () =>
              logout({
                logoutParams: {
                  returnTo: window.location.origin,
                },
              }),
          },
        ],
      },
    ];

    const filterNavigation = (nav: Array<NavigationList>) =>
      nav
        .filter((group) => !group.isHidden)
        .map((group) => ({
          ...group,
          links: group.links.filter((link) => !link.isHidden),
        }));

    return {
      footerNavigation: footerNavigation.filter((nav) => !nav.isHidden),
      navigation: filterNavigation(isEndConsumer ? consumerNavigation : supplierNavigation),
    };
  }, [
    allocationsPivotTableEnabled,
    dealsEnabled,
    esnaEnabled,
    formatMessage,
    isEndConsumer,
    lastAllocatedVolumesTab,
    lastDeliveryMonitoringTab,
    lastDemandTab,
    lastOutboundDeliveryMonitoringTab,
    lastPortfolioTab,
    lastSupplyTab,
    logout,
    pnlAndValuationEnabled,
    toggleChat,
  ]);
}
